import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { SectionHead } from "./SectionHead";

// The Listener — R1 feedback capture + review-only clustering (R1-LS-01/02/03). Clusters group feedback
// that points at the same subject; a cluster below the configured threshold reads "not yet a theme" and
// says how far below it sits. The Listener clusters and summarises — it never decides what to build; the
// roadmap status on a cluster is a human's call, recorded here.
const SOURCE_LABEL = {
  staff_call_note: "staff call note",
  building_or_mark_note: "building / mark note",
  mark_challenge_ref: "mark challenge",
  in_product_form: "in-product form",
};

function Cluster({ c, statuses, onRoadmap, onDismiss }) {
  return (
    <div className={`ls-cluster ${c.below_theme ? "ls-below" : ""}`} data-testid={`ls-cluster-${c.subject_ref}`}>
      <div className="ls-cluster-head">
        <span className="ls-cluster-subject">{c.subject_ref}</span>
        <span className="ls-cluster-size">{c.size} {c.size === 1 ? "note" : "notes"}</span>
        {c.below_theme && (
          <span className="ls-not-theme" data-testid={`ls-not-theme-${c.subject_ref}`}>
            not yet a theme · {c.size} of {c.threshold} needed · {c.below_by} below the threshold
          </span>
        )}
      </div>
      <ul className="ls-items">
        {c.items.map((it, i) => (
          <li key={i} className="ls-item">
            <span className="ls-item-verbatim">“{it.verbatim}”</span>
            <span className="ls-item-src">{SOURCE_LABEL[it.source] || it.source}</span>
          </li>
        ))}
      </ul>
      <div className="ls-cluster-actions">
        <label className="ls-roadmap">roadmap
          <select className="intent-input ls-select" value={c.roadmap_status}
                  data-testid={`ls-roadmap-${c.subject_ref}`}
                  onChange={(e) => onRoadmap(c.subject_ref, e.target.value)}>
            {statuses.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
        </label>
        <button className="ls-dismiss" data-testid={`ls-dismiss-${c.subject_ref}`}
                onClick={() => onDismiss(c.subject_ref)}>dismiss</button>
      </div>
    </div>
  );
}

export default function TheListener() {
  const { authedFetch } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ source: "staff_call_note", verbatim: "", subject_ref: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    authedFetch("/listener/section").then(setD).catch((e) => setErr(String(e)));
  }, [authedFetch]);
  useEffect(load, [load]);

  async function capture(e) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await authedFetch("/listener/feedback", { method: "POST",
        body: { source: form.source, verbatim: form.verbatim, subject_ref: form.subject_ref || null } });
      setForm({ ...form, verbatim: "", subject_ref: "" });
      load();
    } catch (e2) { setErr(String(e2.message || e2)); }
    setBusy(false);
  }

  async function onRoadmap(subject_ref, status) {
    await authedFetch("/listener/cluster/roadmap", { method: "POST", body: { subject_ref, status } });
    load();
  }
  async function onDismiss(subject_ref) {
    await authedFetch("/listener/cluster/dismiss", { method: "POST", body: { subject_ref } });
    load();
  }

  if (err && !d) return <div className="gk-err" data-testid="listener-error">{err}</div>;
  if (!d) return <div className="loading" data-testid="listener-loading">Loading…</div>;

  return (
    <div data-testid="the-listener">
      <SectionHead eyebrow="THE LISTENER · WHAT PEOPLE SAID" testid="ls"
        headline={d.clusters.length > 0
          ? `${d.clusters.length} ${d.clusters.length === 1 ? "thing" : "things"} people said, clustered.`
          : "Nobody has said anything yet."}>
        <p className="cc-constraint" data-testid="listener-constraint">{d.constraint}</p>
      </SectionHead>

      <div className="ls-boundary" data-testid="listener-boundary">{d.boundary}</div>

      <form className="ls-capture" onSubmit={capture} data-testid="listener-capture-form">
        <div className="ls-capture-row">
          <select className="intent-input ls-select" value={form.source} data-testid="ls-source"
                  onChange={(e) => setForm({ ...form, source: e.target.value })}>
            {d.capture_sources.map((s) => <option key={s} value={s}>{SOURCE_LABEL[s] || s}</option>)}
          </select>
          <input className="intent-input" placeholder="subject (a building or mark) — optional"
                 value={form.subject_ref} data-testid="ls-subject"
                 onChange={(e) => setForm({ ...form, subject_ref: e.target.value })} />
        </div>
        <textarea className="intent-input ls-verbatim" placeholder="What did they actually say? Verbatim."
                  value={form.verbatim} data-testid="ls-verbatim" rows={2}
                  onChange={(e) => setForm({ ...form, verbatim: e.target.value })} />
        <button className="ls-submit" type="submit" disabled={busy || !form.verbatim.trim()} data-testid="ls-capture-submit">
          {busy ? "Logging…" : "Log feedback"}
        </button>
        {err && <span className="ls-form-err" data-testid="ls-form-err">{err}</span>}
      </form>

      {!d.present ? (
        <div className="cc-empty" data-testid="listener-empty">{d.absent_reason}</div>
      ) : (
        <div className="ls-clusters" data-testid="listener-clusters">
          {d.clusters.map((c) => (
            <Cluster key={c.subject_ref} c={c} statuses={d.roadmap_statuses}
                     onRoadmap={onRoadmap} onDismiss={onDismiss} />
          ))}
          {d.ungrouped.length > 0 && (
            <div className="ls-ungrouped" data-testid="listener-ungrouped">
              <h3 className="tr-sub-title">Ungrouped signals — no subject yet</h3>
              <ul className="ls-items">
                {d.ungrouped.map((it, i) => (
                  <li key={i} className="ls-item">
                    <span className="ls-item-verbatim">“{it.verbatim}”</span>
                    <span className="ls-item-src">{SOURCE_LABEL[it.source] || it.source}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="ls-sources" data-testid="listener-sources">
        <span className="cc-metric-label">Read-only source</span>
        <div className="ls-sources-body">
          <strong>{d.sources.mark_challenges}</strong> mark challenge{d.sources.mark_challenges === 1 ? "" : "s"} · {d.sources.note}
        </div>
      </div>
    </div>
  );
}
